<template>
  <v-container fluid>
    <v-row class="ml-1">
      <global-stallholder-single-select
        :showStalls="true"
        @stallholder-changed="handleStallholderChange"
        @stall-changed="handleStallChange"
      />
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense>mdi-home</v-icon>
            Terminals
            <v-spacer />
            <v-btn
              x-small
              dark
              @click="handleAddTerminal"
              :disabled="!currentStall || !currentStall.StallId"
            >
              <v-icon left x-small>mdi-plus</v-icon>
              Add Terminal
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-0">
            <!-- Filter Section -->

            <!-- Data Table -->
            <v-data-table
              :headers="tableHeaders"
              :items="currentTerminals"
              sort-by="TerminalId"
              single-select
              return-object
              class="elevation-5"
            >
              <template v-slot:item.TerminalNumber="{ item }">
                {{ formatField(item.TerminalNumber) }}
              </template>

              <template v-slot:item.OrderNumber="{ item }">
                {{ formatField(item.OrderNumber) }}
              </template>

              <template v-slot:item.PaymentProfile="{ item }">
                {{ formatArrayField(item.PaymentProfile) }}
              </template>

              <template v-slot:item.PrintingProfile="{ item }">
                {{ formatField(item.PrintingProfile) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <v-icon small class="mr-2" @click="editTerminal(item)">
                  mdi-pencil
                </v-icon>
                <v-icon small @click="deleteTerminal(item)">
                  mdi-delete
                </v-icon>
              </template>

              <template v-slot:no-data>
                <v-btn color="primary">No terminals for this stall</v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Delete Confirmation Dialog -->
    <v-dialog dark v-model="showDeleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          Are you sure you want to delete this Terminal?
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDeleteDialog">
            Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="confirmDelete"> OK </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import "izitoast/dist/css/iziToast.min.css";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import { mapState, mapActions, mapMutations } from "vuex";

export default {
  name: "Terminals",

  data() {
    return {
      showDeleteDialog: false,
      terminalToDelete: null,
      deleteIndex: -1,

      tableHeaders: [
        { text: "Terminal Number", value: "TerminalNumber", width: "20%" },
        { text: "Order Prefix", value: "OrderPrefix" },
        { text: "Payment Profile", value: "PaymentProfile", width: "20%" },
        { text: "Printing Profile", value: "PrintingProfile", width: "20%" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },

  computed: {
    ...mapState("admin", [
      "currentEvent",
      "currentStallHolders",
      "currentStallholder",
      "currentStall",
    ]),

    // Computed property for current terminals that automatically updates when store changes
    currentTerminals() {
      // If we have a current stall selected, always get the most up-to-date data
      if (this.currentStall && this.currentStall.StallId) {
        // First try to get fresh data from currentStallHolders (most up-to-date)
        if (
          this.currentStallHolders &&
          this.currentStallHolders.length > 0 &&
          this.currentStallholder
        ) {
          const freshStallholder = this.currentStallHolders.find(
            (sh) => sh.StallholderId === this.currentStallholder.StallholderId
          );
          if (freshStallholder?.Stalls) {
            const freshStall = freshStallholder.Stalls.find(
              (s) => s.StallId === this.currentStall.StallId
            );
            if (freshStall?.Terminals) {
              return freshStall.Terminals;
            }
          }
        }

        // Fallback to currentStall.Terminals
        if (this.currentStall.Terminals) {
          return this.currentStall.Terminals;
        }
      }

      return [];
    },
  },

  watch: {
    showDeleteDialog(val) {
      if (!val) {
        this.closeDeleteDialog();
      }
    },

    currentStallholder: {
      handler(newStallholder, oldStallholder) {
        // On initial load (immediate: true), only set stall if none is currently selected
        if (!oldStallholder && this.currentStall && this.currentStall.StallId) {
          // Component is initializing but we already have a stall selected, don't change it
          return;
        }

        // Only reset stall selection if the stallholder actually changed (not just updated)
        const stallholderChanged =
          oldStallholder &&
          oldStallholder.StallholderId !== newStallholder?.StallholderId;

        if (!oldStallholder || stallholderChanged) {
          // Initial load or stallholder actually changed, reset to first stall
          if (newStallholder?.Stalls && newStallholder.Stalls.length > 0) {
            this.setCurrentStall(newStallholder.Stalls[0]);
          } else {
            this.setCurrentStall({});
          }
        } else if (newStallholder?.Stalls && this.currentStall?.StallId) {
          // Same stallholder but data refreshed, try to maintain current stall selection
          const updatedStall = newStallholder.Stalls.find(
            (stall) => stall.StallId === this.currentStall.StallId
          );
          if (updatedStall) {
            // Update the current stall with fresh data
            this.setCurrentStall(updatedStall);
          } else if (newStallholder.Stalls.length > 0) {
            // Current stall no longer exists, fallback to first stall
            this.setCurrentStall(newStallholder.Stalls[0]);
          } else {
            this.setCurrentStall({});
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapMutations("admin", ["setCurrentStallholder", "setCurrentStall"]),
    ...mapActions("admin", ["createEvent", "getEventMetadata"]),

    // Event Handlers
    handleStallholderChange(stallholder) {
      if (stallholder) {
        this.setCurrentStallholder(stallholder);
        // The watcher will handle setting the first stall automatically
      }
    },

    handleStallChange(stall) {
      if (stall) {
        this.setCurrentStall(stall);
      }
    },

    handleAddTerminal() {
      this.navigateToTerminalConfig(0, {
        isNew: true,
        stallData: this.currentStall,
        stallholderData: this.currentStallholder,
      });
    },

    editTerminal(terminal) {
      this.navigateToTerminalConfig(terminal.TerminalId, {
        isNew: false,
        terminalData: terminal,
        stallData: this.currentStall,
        stallholderData: this.currentStallholder,
      });
    },

    deleteTerminal(terminal) {
      this.terminalToDelete = { ...terminal };
      this.deleteIndex = this.currentTerminals.indexOf(terminal);
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      try {
        if (this.terminalToDelete && this.terminalToDelete.TerminalId) {
          const eventItem = [
            {
              Type: "Terminal",
              Action: "delete",
              Properties: this.terminalToDelete,
            },
          ];

          const submitEvent = { event_items: eventItem };

          await this.createEvent(submitEvent);

          // Refresh the admin state to get updated data from server
          if (this.currentEvent?.EventId) {
            await this.getEventMetadata(this.currentEvent.EventId);
          }

          this.showSuccessMessage("Terminal deleted successfully!");
        }
      } catch (error) {
        console.error("Delete terminal error:", error);
        this.showErrorMessage("Failed to delete terminal!");
      } finally {
        this.closeDeleteDialog();
      }
    },

    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.terminalToDelete = null;
      this.deleteIndex = -1;
    },

    // Utility Methods
    formatField(value) {
      return value || "No data available";
    },

    formatArrayField(array) {
      return array && array.length > 0 ? array.join(", ") : "No data available";
    },

    navigateToTerminalConfig(id, data = {}) {
      this.$router.push({
        name: "Terminal Configuration",
        params: {
          id,
          ...data,
        },
      });
    },

    showSuccessMessage(message) {
      iziToast.success({
        title: "Terminal",
        message,
        position: "topRight",
      });
    },

    showErrorMessage(message) {
      iziToast.warning({
        title: "Terminal",
        message,
        position: "topRight",
      });
    },
  },
};
</script>

<style scoped>
h1 {
  color: #a09c94;
  font-family: sans-serif;
}
</style>
